




























import { ColorSwatchIcon } from '@vue-hero-icons/outline';
import { computed, defineComponent, PropType, Ref } from '@vue/composition-api';
import { isNil } from 'ramda';
import { BlockCategory, BlockType, ExecutionType } from '../../constants';
import { SampleConfiguration } from '../../types';

export default defineComponent({
    name: 'SampleDescription',
    props: {
        configuration: {
            type: Object as PropType<SampleConfiguration>,
            required: true,
        },
        sourceTask: {
            type: Object as PropType<{ id: string; displayName: string }>,
            required: true,
        },
        executionType: {
            type: String,
            required: true,
        },
        blockCategory: { type: String as PropType<BlockCategory>, required: true },
        blockType: { type: String as PropType<BlockType>, required: false },
        isInLoop: { type: Boolean, default: false },
    },
    components: { ColorSwatchIcon },
    setup(props, { emit }) {
        const isEvaluateInLoop = computed(
            () =>
                props.blockCategory === BlockCategory.MachineLearning &&
                !isNil(props.blockType) &&
                props.blockType === BlockType.Evaluate &&
                props.isInLoop,
        );

        const usesAllOfData = computed(
            () =>
                props.blockCategory === BlockCategory.MachineLearning &&
                !isNil(props.blockType) &&
                [BlockType.Evaluate, BlockType.Train].includes(props.blockType),
        );

        const message = computed(() => {
            if (isEvaluateInLoop.value)
                return `Results aggregated from all for-loop iterations, obtained through ${props.executionType} execution.`;
            if (props.executionType === ExecutionType.Dry) {
                return `Sample obtained through dry execution.`;
            }
            return `Sample obtained through test execution performed for task '${props.sourceTask.displayName}'.`;
        });

        const sampleDescription = computed(() => {
            if (usesAllOfData.value) return `Bypassing configured sample and using 100% of the data`;
            if (props.executionType === ExecutionType.Dry) return `Using 200 first rows`;
            if (props.configuration.size.rowsCount)
                return `Configured sampling: ${props.configuration.selection} ${props.configuration.size.rowsCount} rows`;
            return `Configured sampling: ${props.configuration.selection} ${
                (props.configuration.size.percentage ?? 0.1) * 100
            }%`;
        });

        const description = computed(() => `${message.value} (${sampleDescription.value})`);

        const tooltip: Ref<{ message: string; click?: any | undefined } | undefined> = computed(() => {
            if (isEvaluateInLoop.value)
                return {
                    message: `The results passed to subsequent blocks in each iteration contain only the data in the row that corresponds to each loop iteration.`,
                };
            if (usesAllOfData.value) return undefined;
            if (!props.configuration.usingOptimisedParquet)
                return {
                    message: `Go to the analytics pipeline's configuration to amend this.`,
                    click: () => emit('show-configuration'),
                };
            return undefined;
        });

        return { description, tooltip };
    },
});
